/**
 * COMMERCE-176D-R1 — promotions-v1 overlay for PDP/cards (same source as promo strip).
 * Never invents old_price; only applies when promotions-v1 has old_price_uah > price_uah.
 */
type PromoPriceRow = {
  product_id: string;
  price_uah: number;
  old_price_uah?: number;
};

let cache: Map<string, PromoPriceRow> | null = null;
let inflight: Promise<Map<string, PromoPriceRow>> | null = null;

async function loadMap(): Promise<Map<string, PromoPriceRow>> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = fetch(`/data/promotions-v1.json`, { cache: "force-cache" })
    .then((r) => (r.ok ? r.json() : null))
    .then((doc) => {
      const map = new Map<string, PromoPriceRow>();
      const list = Array.isArray(doc?.products) ? (doc.products as PromoPriceRow[]) : [];
      for (const row of list) {
        const id = String(row.product_id ?? "").trim();
        if (!id) continue;
        const price = Number(row.price_uah);
        if (!(price >= 1)) continue;
        const old =
          typeof row.old_price_uah === "number" && row.old_price_uah > price
            ? row.old_price_uah
            : undefined;
        map.set(id, {
          product_id: id,
          price_uah: price,
          ...(old != null ? { old_price_uah: old } : {}),
        });
      }
      cache = map;
      return map;
    })
    .catch(() => {
      cache = new Map();
      return cache;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** Warm cache early (Home / Promotions). Safe to call many times. */
export function ensurePromoPriceOverlayLoaded(): void {
  void loadMap();
}

export function getPromoOverlayPrices(
  productId: string,
): { price_uah: number; old_price_uah?: number } | null {
  if (!cache) {
    void loadMap();
    return null;
  }
  return cache.get(String(productId)) ?? null;
}

export async function awaitPromoOverlayPrices(
  productId: string,
): Promise<{ price_uah: number; old_price_uah?: number } | null> {
  const map = await loadMap();
  return map.get(String(productId)) ?? null;
}
